import React from 'react'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import { DocumentControlButton } from '@/modules/base/components/buttons/index'
import LinkedIcon from '@assets/images/icons/v2/linked.svg'
import UnlinkIcon from '@assets/images/icons/v2/unlink.svg'
import TrashcanIconSvg from '@assets/images/icons/trashcan.svg'
import ThemeInterface from '@/theme-interface'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  documentButton: {
    background: theme.white,
  },
}))

interface ReceiptRemoveButtonProps {
  // Kept as loose as in `ReceiptPreviewerItemProps`, which this was lifted out of: the hosts pass a
  // receipt, an assigned file or a plain file here, and only `isAssigned` is ever read.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any,
  hideRemove?: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDelete?: (item: any) => void,
}

/**
 * The receipt preview's ONLY „Dokument entfernen" control: „Verknüpfung lösen" for a file that is
 * merely assigned to the receipt (`item.isAssigned`), „Löschen" for one that belongs to it,
 * nothing at all where the host suppresses removal (`hideRemove`).
 *
 * Lifted verbatim out of `receipt-previewer-item.tsx`, because that item is exactly what an
 * e-invoice slide REPLACES: FLAG:useDocuAi a receipt whose DMS document has children (extracted
 * XML, KoSIT report, …) renders the artifact switcher instead of the item — and with the item went
 * the only way to detach the document („ausgaben mit e-rechnung -> dokument kann nicht entfernt
 * werden", QA 2026-09-15). The artifact preview now mounts this very component, so the button
 * appears in a new PLACE under unchanged RULES; a second implementation would have drifted.
 *
 * It lives in its own module on purpose: the preview item drags `redux/store` behind it (via
 * positions-with-tax-category), which cannot even be loaded in a jest suite — the button has to be
 * reachable without it.
 */
const ReceiptRemoveButton: React.FC<ReceiptRemoveButtonProps> = ({ item, hideRemove, onDelete }) => {
  const classes = useStyles()

  if (hideRemove) {
    return null
  }

  if (item?.isAssigned) {
    return (
      <DocumentControlButton
        dataQsId='receipt-preview-unlink'
        title={lang.expense.previewToolbar.items.unlink}
        icon={LinkedIcon}
        iconActive={UnlinkIcon}
        customClass={classes.documentButton}
        onClick={() => {
          onDelete(item)
        }}
      />
    )
  }

  return (
    <DocumentControlButton
      dataQsId='receipt-preview-delete'
      title={lang.expense.previewToolbar.items.delete}
      icon={TrashcanIconSvg}
      customClass={classes.documentButton}
      onClick={() => {
        onDelete(item)
      }}
    />
  )
}

export default ReceiptRemoveButton
